import { gql } from '@apollo/client'
import appConfig from '../config'
import API from '../API'
import { v4 as uuidv4 } from 'uuid'

const { CLOUDINARY_DATA_EXTRACTION_URL } = appConfig

const generateSignedUploadRequestMutation = gql`
  mutation generateSignedUploadRequest(
    $publicId: String
    $resourceType: String
    $caption: String
    $moderation: String
    $notificationUrl: String
    $raw_convert: String
    $filename: String
  ) {
    generateSignedUploadRequest(
      publicId: $publicId
      resourceType: $resourceType
      caption: $caption
      moderation: $moderation
      notificationUrl: $notificationUrl
      raw_convert: $raw_convert
      filename: $filename
    ) {
      api_url
      signature
      api_key
      timestamp
      public_id
      raw_convert
    }
  }
`
interface UploadOptions {
  moderation?: string | null | undefined
  resourceType?: string | null | undefined
  publicId?: string | null | undefined
  raw_convert?: string | null | undefined
}

const uploadToCloudinary = (
  file,
  folder: string | null | undefined = null,
  onProgress,
  signedValues: UploadOptions = {}
) => {
  return new Promise(async (resolve, reject) => {
    let variables: any = {
      caption: file.name,
      filename: file.name,
      ...signedValues
    }
    if (folder) {
      variables.publicId = `${folder}/${uuidv4()}`
    }

    if (CLOUDINARY_DATA_EXTRACTION_URL) {
      variables.notificationUrl = CLOUDINARY_DATA_EXTRACTION_URL
    }

    // The executor is async, so anything thrown in it is lost rather than
    // rejecting this promise -- which would leave callers waiting forever.
    // Catch it and reject explicitly (here, in uploadChunk, and in the
    // success handler below).
    let signedUploadRequest
    try {
      const uploadRequestResult = await API.mutate({
        mutation: generateSignedUploadRequestMutation,
        variables,
        fetchPolicy: 'no-cache'
      })
      signedUploadRequest =
        uploadRequestResult?.data?.generateSignedUploadRequest
    } catch (err) {
      reject(err)
      return
    }
    if (!signedUploadRequest) {
      reject(new Error('Could not get a signed upload request'))
      return
    }

    const uniqueUploadId = `uqid-${Date.now()}`
    const chunkSize = 20 * 1024 * 1024
    const totalChunks = Math.ceil(file.size / chunkSize)
    let currentChunk = 0
    const uploadChunk = async (start, end) => {
      const xhr = new XMLHttpRequest()
      const form = new FormData()

      xhr.open('POST', signedUploadRequest.api_url, true)
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')

      xhr.upload.addEventListener('progress', e => {
        onProgress(
          Math.round(((currentChunk + e.loaded / e.total) / totalChunks) * 100)
        )
      })

      const contentRange = `bytes ${start}-${end - 1}/${file.size}`
      xhr.setRequestHeader('Content-Range', contentRange)
      xhr.setRequestHeader('X-Unique-Upload-Id', uniqueUploadId)
      form.append('timestamp', signedUploadRequest.timestamp)
      form.append('signature', signedUploadRequest.signature)
      form.append('api_key', signedUploadRequest.api_key)
      form.append('context', `caption=${file.name}`)
      form.append('filename_override', file.name)
      form.append('file', file.slice(start, end))

      if (signedUploadRequest.public_id) {
        form.append('public_id', signedUploadRequest.public_id)
      }

      if (signedUploadRequest.raw_convert) {
        form.append('raw_convert', signedUploadRequest.raw_convert)
      }

      if (signedValues?.moderation) {
        form.append('moderation', signedValues.moderation)
      }

      if (
        CLOUDINARY_DATA_EXTRACTION_URL &&
        (signedUploadRequest.raw_convert || signedValues?.moderation)
      ) {
        form.append('notification_url', CLOUDINARY_DATA_EXTRACTION_URL)
      }

      console.log(
        `Uploading chunk for uniqueUploadId: ${uniqueUploadId}; start: ${start}, end: ${
          end - 1
        }`
      )
      xhr.onreadystatechange = () => {
        console.log(`XHR state ${xhr.readyState}, status ${xhr.status}`)

        if (xhr.readyState !== 4) return

        if (xhr.status === 200) {
          currentChunk++
          if (currentChunk < totalChunks) {
            const nextStart = currentChunk * chunkSize
            const nextEnd = Math.min(nextStart + chunkSize, file.size)
            uploadChunk(nextStart, nextEnd).catch(reject)
          } else {
            try {
              resolve({ ...JSON.parse(xhr.responseText), filename: file.name })
              console.info('File upload complete.')
            } catch (err) {
              reject(err)
            }
          }
        } else {
          try {
            const message = JSON.parse(xhr.response).error.message
            reject(message)
          } catch (err) {
            reject(xhr.statusText)
          }
        }
      }
      xhr.send(form)
    }
    const start = 0
    const end = Math.min(chunkSize, file.size)
    uploadChunk(start, end).catch(reject)
  })
}

export default uploadToCloudinary
